<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <span class="headline mx-2">Merit/Demerit Incidents</span>
        <employee-incidents-dialog-create 
          v-if="$hasPermissionTo(['create incidents'])"
          :employee-uid="employeeUid"
          @created="loadIncidents"
        />
      </v-col>
      <v-col
        v-if="noOfIncidents > 0"
        cols="12"        
      >
        <div
          data-simplebar
          class="overflow"
        >
          <v-timeline
            :dense="$vuetify.breakpoint.lgAndDown"
          >
            <v-timeline-item
              v-for="(incident, i) in incidents"
              :key="i"
              :color="getIncidentColor(incident.type.name)"
              :left="isLeft(incident.type.name)"
              :right="!isLeft(incident.type.name)"
              small
            >
              <template v-slot:opposite>
                <span :class="`headline font-weight-bold ${getIncidentColor(incident.type.name)}--text`"> 
                  {{ $moment(incident.created_at, 'YYYY-MM-DD').format('DD MMM YYYY') }} 
                </span>
              </template>
              <v-card
                :color="getIncidentColor(incident.type.name)"
                dark
              >
                <v-list
                  two-line
                  :color="getIncidentColor(incident.type.name)"
                >
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium text-capitalize">
                        {{ incident.subject }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-uppercase pt-1">
                        by {{ incident.created_by }} {{ $vuetify.breakpoint.lgAndDown ? $moment(incident.created_at, 'YYYY-MM-DD').format('DD MMM YYYY') : '' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action 
                      v-if="$hasPermissionTo(['delete incidents']) || 
                        $hasPermissionTo(['update incidents'])"
                    >
                      <v-menu
                        bottom
                        right
                        nudge-right="30px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            icon
                            v-on="on"                        
                          >
                            <v-icon>more_vert</v-icon>
                          </v-btn>
                        </template>
                  
                        <v-list>
                          <v-list-item
                            v-if="$hasPermissionTo(['update incidents'])"                          
                            @click="$refs.editDialog.toggleDialog(incident)"
                          >
                            <v-list-item-title>                             
                              Edit
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-if="$hasPermissionTo(['update incidents'])"                          
                            @click="$refs.uploadDialog.toggleDialog(incident)"
                          >
                            <v-list-item-title>                             
                              Add File
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-if="$hasPermissionTo(['delete incidents'])"
                            @click="$refs.deleteDialog.toggleDialog(incident)"
                          >
                            <v-list-item-title>
                              Delete
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <v-card-text
                  class="white text--primary text-justify pt-0"
                  style="white-space: pre-line;"
                >
                  <p>
                    {{ 
                      truncate[incident.id] ? 
                        getTruncatedDescription(incident.description) : 
                        incident.description 
                    }}
                  </p>
                  <div v-if="requireTruncation(incident.description)">
                    <a 
                      v-if="truncate[incident.id]" 
                      @click.stop="$set(truncate, incident.id, false)"
                    >Show more ...</a> 
                    <a 
                      v-else 
                      @click.stop="$set(truncate, incident.id, true)"
                    >Show less ...</a>
                  </div>
                </v-card-text>

                <div v-if="incident.documents.length > 0">
                  <v-divider />

                  <v-card-subtitle class="font-weight-bold white black--text pb-0">
                    {{ incident.documents.length }} documents
                  </v-card-subtitle>
                </div>

                <v-container class="white py-0 px-4">
                  <v-row>
                    <template v-for="document in incident.documents">
                      <v-col 
                        :key="document.id"
                        cols="6"
                        md="4"
                        class="px-1 py-2"
                      >
                        <v-chip
                          color="blue-grey lighten-3"
                          link
                          style="width: 100%;"
                          :href="document.file_url"
                          target="_blank"
                        >
                          <v-icon
                            color="red"
                            left
                          >
                            mdi-file-pdf
                          </v-icon>
                          <span class="text-truncate"> {{ document.file_name }} </span>
                        </v-chip>
                      </v-col>
                    </template> 
                  </v-row>
                </v-container>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </div>
        <employee-incidents-dialog-edit
          ref="editDialog"
          :employee-uid="employeeUid"
          @updated="loadIncidents"
        />
        <employee-incidents-dialog-upload
          ref="uploadDialog"
          :employee-uid="employeeUid"
          @uploaded="loadIncidents"
        />
        <employee-incidents-dialog-delete
          ref="deleteDialog"
          :employee-uid="employeeUid"
          @deleted="loadIncidents"
        />
      </v-col>
      <v-col
        v-else
        cols="12"
      >
        <span class="mx-2"> No data available </span>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  export default {
    components: {
      EmployeeIncidentsDialogDelete: () => import('@/components/employee/incidents/DialogDelete'),
      EmployeeIncidentsDialogUpload: () => import('@/components/employee/incidents/DialogUpload'),
      EmployeeIncidentsDialogEdit: () => import('@/components/employee/incidents/DialogEdit'),
      EmployeeIncidentsDialogCreate: () => import('@/components/employee/incidents/DialogCreate'),
    },
    props: {
      employeeUid: {
        type: String, 
        default: '',
      },
    },
    data () {
      return {
        incidents: [],
        truncate: [],
        loading: false,
      }
    },
    computed: {
      noOfIncidents () {
        return this.incidents.length
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.incidents.splice(0, this.incidents.length)
            this.loadIncidents()
          }
        },
      },
    },
    methods: {
      loadIncidents () {
        this.loading = true

        this.$api.incidents().list(this.employeeUid)
          .then(response => {
            this.incidents = response.data
            this.setTruncation(this.incidents)
            
            this.loading = false
          })
      },
      setTruncation (incidents, length = 500) {
        incidents.map(incident => {
          if (incident.description.length > length) {
            this.$set(this.truncate, incident.id, true)
          }
        })
      },
      getTruncatedDescription (description, length = 500, ending = '...') {
        if (description.length > length) {
          return description.substring(0, length - ending.length) + ending
        }

        return description
      },
      requireTruncation (description, length = 500) {
        return description.length > length
      },
      getIncidentColor (type) {
        return type === 'merit' ? 'info' : 'error'
      },
      isLeft (type) {
        return type === 'merit'
      },
    },
  }
</script>

<style lang="css" scoped>
  .overflow {
    height:75vh;
  }
</style>
